import { z } from "zod";

export const UpdateZapParams = z.object({
  zap_id: z.string().describe("REQUIRED: The ID of the Zap to update."),
  account_id: z
    .string()
    .describe(
      "REQUIRED: The account ID owning the Zap. This MUST be sourced from 'additionalData.canvas.account_id'. Do NOT ask the user for this value.",
    ),
  app: z
    .string()
    .describe(
      "REQUIRED: The ZDL app type for the Zap (e.g., 'EngineAPI'). Typically 'EngineAPI'.",
    ),
  action: z
    .string()
    .describe(
      "REQUIRED: The ZDL action for the Zap (e.g., 'series_skip_errors'). Typically 'series_skip_errors'.",
    ),
  steps: z
    .array(
      z.object({
        id: z.string().describe("REQUIRED: The ID of the step."),
        app: z
          .string()
          .describe(
            "REQUIRED: The short app name for this step (e.g., 'gmail', 'slack'). If app_id is versioned, this should be the base name.",
          ),
        app_id: z
          .string()
          .describe(
            "REQUIRED: The full app ID for this step, which may include a version (e.g., 'TableCLIAPI@1.16.5' or 'slack'). Use the get_zap tool first to retrieve the correct app_id format for each existing step. This is CRITICAL for the update to succeed.",
          ),
        type: z
          .string()
          .describe(
            "REQUIRED: The type of step (e.g., 'read', 'write', 'filter').",
          ),
        action: z
          .string()
          .describe(
            "REQUIRED: The specific action key for this step (e.g., 'event_start', 'direct_message').",
          ),
        params: z
          .record(z.any())
          .describe("REQUIRED: The parameters object for this step."),
        meta: z
          .record(z.any())
          .optional()
          .describe("Optional: Metadata object for this step."),
        authentication_id: z
          .number()
          .int()
          .optional()
          .describe(
            "Optional: Authentication ID for this step, if applicable.",
          ),
        title: z.string().optional().describe("Optional: Title for this step."),
      }),
    )
    .min(1)
    .describe(
      "REQUIRED: The array of step objects defining the Zap's workflow. Must contain at least one step.",
    ),
  timezone: z
    .string()
    .optional()
    .describe(
      "Optional: The timezone for the Zap (e.g., 'America/Los_Angeles').",
    ),
  title: z
    .string()
    .optional()
    .describe("Optional: The overall title of the Zap."),
  description: z
    .string()
    .optional()
    .describe("Optional: The description of the Zap."),
  zap_note: z
    .string()
    .nullable()
    .optional()
    .describe("Optional: Notes about the Zap."),
  active: z
    .boolean()
    .optional()
    .describe(
      "Optional: Whether the Zap should be active (true) or inactive (false).",
    ),
});

export type UpdateZapParamsType = z.infer<typeof UpdateZapParams>;

export const updateZapExecute = async (rawArgs: any) => {
  let params: UpdateZapParamsType;
  try {
    params = UpdateZapParams.parse(rawArgs || {});
  } catch (error) {
    console.error("[UPDATE_ZAP_TOOL] Error parsing arguments:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error: Invalid arguments provided for update_zap. ${error instanceof Error ? error.message : "Unknown parsing error."}`,
        },
      ],
    };
  }

  const missingAppIdSteps = params.steps.filter((step) => !step.app_id);
  if (missingAppIdSteps.length > 0) {
    const errorText = `Error: All steps must include an 'app_id'. Missing for step IDs: ${missingAppIdSteps.map((s) => s.id).join(", ")}. Use get_zap to fetch current app_ids.`;
    console.error("[UPDATE_ZAP_TOOL] Validation Error:", errorText);
    return { content: [{ type: "text", text: errorText }] };
  }

  const { zap_id, account_id, ...bodyForProxy } = params;

  const queryForProxy = new URLSearchParams({ zap_id, account_id });
  const proxyApiUrl = `/api/canvasui/composer/updateZap?${queryForProxy.toString()}`;

  try {
    const res = await fetch(proxyApiUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(bodyForProxy),
    });

    if (!res.ok) {
      let errorData: any = {
        error: `Proxy error for update_zap (${res.status}) - Unknown error structure`,
      };
      try {
        errorData = await res.json();
        if (!(errorData && errorData.error)) {
          errorData = {
            error: `Proxy error for update_zap (${res.status}) - Received non-standard error`,
            details: errorData,
          };
        }
      } catch (e) {
        errorData = {
          error: `Proxy error for update_zap (${res.status}) - Response not valid JSON`,
        };
      }
      const errorDetailString =
        errorData.error +
        (errorData.details ? `: ${JSON.stringify(errorData.details)}` : "");
      console.error(
        `[UPDATE_ZAP_TOOL] Proxy error for URL ${proxyApiUrl}: ${errorDetailString}`,
      );
      throw new Error(errorDetailString);
    }

    try {
      const data = await res.json();
      return {
        content: [
          {
            type: "text",
            text: JSON.stringify(data, null, 2),
          },
        ],
      };
    } catch (e) {
      console.error(
        `[UPDATE_ZAP_TOOL] Error parsing successful proxy response as JSON. URL: ${proxyApiUrl}, Status: ${res.status}. Error: ${e instanceof Error ? e.message : String(e)}`,
      );
      throw new Error(
        `Proxy returned success status (${res.status}) but its response body was not valid JSON.`,
      );
    }
  } catch (error) {
    console.error("[UPDATE_ZAP_TOOL] Error in updateZapExecute:", error);
    return {
      content: [
        {
          type: "text",
          text: `Error updating Zap: ${error instanceof Error ? error.message : String(error)}`,
        },
      ],
    };
  }
};

export const updateZapDescription = `Update an existing Zap with a new configuration. 
IMPORTANT: This tool requires the complete desired state of the Zap, including all steps with their correct 'app_id', parameters, and other settings. 
It is STRONGLY recommended to call 'get_zap' first to fetch the current Zap structure if you are modifying an existing Zap. Use that structure as a basis for your changes. Ensure every step in the 'steps' array has a valid 'app_id'.
It is also recommended to call 'validate_zsl' with the proposed ZSL for the Zap to ensure the updated configuration will be valid before attempting the update.

Required parameters: 
- zap_id: The ID of the Zap to update. 
- account_id: The ID of the account owning the Zap. This MUST be sourced from 'additionalData.canvas.account_id'. Do NOT ask the user for this value. 
- app: The ZDL app type for the Zap (typically 'EngineAPI'). 
- action: The ZDL action for the Zap (typically 'series_skip_errors'). 
- steps: An array of step objects. Each step object MUST include: 'id', 'app', 'app_id', 'type', 'action', and 'params'.

Optional parameters for the Zap overall: 
- timezone: (e.g., 'America/Los_Angeles') 
- title: The overall title of the Zap. 
- description: The description of the Zap. 
- zap_note: Notes about the Zap. 
- active: Boolean, whether the Zap should be active (true) or inactive (false).

Optional parameters per step (within the steps array objects): 
- meta: Metadata object for the step. 
- authentication_id: Authentication ID for the step. 
- title: Title for the step.`;
